import { copyToClipboard } from '@/pages/aigc/utils/utils';
import { ITableNode, IFindTidsResult, ITidInfo } from '../types';

/**
 * 提取节点中的文本内容（优化版）
 * 优化点：
 * 1. 使用迭代代替递归，避免深层嵌套时的栈溢出
 * 2. 使用数组累积代替字符串拼接，提升性能
 * 3. 添加深度限制，防止无限递归
 * 4. 早期返回，减少不必要的计算
 */
interface ITextExtractionConfig {
  maxDepth?: number;
  maxTextLength?: number;
  detectCycles?: boolean;
}
export const extractTextContent = (
  node: any, 
  config: ITextExtractionConfig = {}
): string => {
  const {
    maxDepth = 50,
    maxTextLength = 100000,  // 最大文本长度限制
    detectCycles = true,
  } = config;
  
  if (!node) return '';
  
  // 直接处理字符串类型
  if (typeof node === 'string') {
    return node.slice(0, maxTextLength);
  }
  
  if (node.value) {
    return String(node.value).slice(0, maxTextLength);
  }
  
  // 没有子节点，直接返回
  if (!node.children || node.children.length === 0) {
    return '';
  }
  
  // 使用 WeakSet 追踪已访问的对象（仅对象类型）
  const visited = detectCycles ? new WeakSet<object>() : null;
  const stack: Array<{ node: any; depth: number; }> = [{ node, depth: 0 }];
  const textParts: string[] = [];
  let totalLength = 0;
  
  while (stack.length > 0) {
    const { node: currentNode, depth } = stack.pop()!;
    
    // 深度限制保护
    if (depth > maxDepth) {
      console.warn(`节点深度超过限制 ${maxDepth}，停止遍历`);
      continue;
    }
    
    // 总长度限制保护
    if (totalLength >= maxTextLength) {
      console.warn(`文本长度超过限制 ${maxTextLength}，停止提取`);
      break;
    }
    
    // 循环引用检测
    if (visited && typeof currentNode === 'object' && currentNode !== null) {
      if (visited.has(currentNode)) {
        console.warn('检测到循环引用，跳过该节点');
        continue;
      }
      visited.add(currentNode);
    }
    
    // 处理当前节点
    if (typeof currentNode === 'string') {
      const text = currentNode.slice(0, maxTextLength - totalLength);
      textParts.push(text);
      totalLength += text.length;
      continue;
    }
    
    if (currentNode?.value) {
      const text = String(currentNode.value).slice(0, maxTextLength - totalLength);
      textParts.push(text);
      totalLength += text.length;
      continue;
    }
    
    // 将子节点按逆序压入栈（保证正确的遍历顺序）
    if (currentNode?.children && Array.isArray(currentNode.children)) {
      // 限制子节点数量，防止恶意构造的超大树
      const childCount = Math.min(currentNode.children.length, 1000);
      for (let i = childCount - 1;i >= 0;i--) {
        stack.push({ 
          node: currentNode.children[i], 
          depth: depth + 1 
        });
      }
    }
  }
  
  return textParts.join('');
};

/**
 * 从AST节点中提取表格数据（优化版）
 * 优化点：
 * 1. 单次遍历同时提取表头和表体，减少遍历次数
 * 2. 使用数组预分配，减少内存重新分配
 * 3. 避免多次 filter 和 map，直接在循环中处理
 * 4. 添加数据量限制，防止内存溢出
 * 5. 早期返回和边界检查，提升性能
 * 
 * 注意：此函数只在操作时调用，不会在渲染时频繁执行
 */
export const extractTableData = (node: ITableNode): string[][] => {
  // 早期返回：节点无效
  if (!node?.children || !Array.isArray(node.children)) {
    return [];
  }
  
  const data: string[][] = [];
  const MAX_ROWS = 10000; // 最大行数限制，防止内存溢出
  const MAX_COLS = 100;   // 最大列数限制
  let rowCount = 0;
  
  // 单次遍历提取表头和表体
  for (const child of node.children) {
    if (!child) continue;
    
    const {tagName} = child;
    
    // 处理表头 (thead)
    if (tagName === 'thead' && child.children) {
      for (const row of child.children) {
        if (row?.tagName === 'tr' && row.children) {
          const headers: string[] = [];
          
          // 直接遍历提取 th 内容
          let cellIndex = 0;
          for (const cell of row.children) {
            if (cellIndex >= MAX_COLS) break;
            if (cell?.tagName === 'th') {
              headers.push(extractTextContent(cell));
            }
            cellIndex += 1;
          }
          
          if (headers.length > 0) {
            data.push(headers);
            rowCount += 1;
          }
        }
      }
    }
    
    // 处理表体 (tbody)
    else if (tagName === 'tbody' && child.children) {
      for (const row of child.children) {
        // 行数限制保护
        if (rowCount >= MAX_ROWS) {
          console.warn(`表格行数超过限制 (${MAX_ROWS})，已截断`);
          return data;
        }
        
        if (row?.tagName === 'tr' && row.children) {
          const rowData: string[] = [];
          
          // 直接遍历提取 td 内容
          let cellCount = 0;
          for (const cell of row.children) {
            if (cellCount >= MAX_COLS) break;
            if (cell?.tagName === 'td') {
              rowData.push(extractTextContent(cell));
            }
            cellCount += 1;
          }
          
          if (rowData.length > 0) {
            data.push(rowData);
            rowCount += 1;
          }
        }
      }
    }
  }
  
  return data;
};

/**
 * 将表格数据转换为TSV格式（Tab分隔）
 * 用于复制到剪贴板，兼容Excel等软件
 */
export const convertToTSV = (data: string[][]): string => data.map(row => row.join('\t')).join('\n');

/**
 * 将表格数据转换为HTML格式
 * 用于在Word中保持表格样式
 * @param data - 表格数据数组
 * @returns HTML格式的表格字符串
 * 
 * 优化点：
 * 1. 表头和单元格设置最小宽度，确保在 Word 中显示足够宽
 * 2. 使用 white-space: nowrap 防止文本换行导致列宽过窄
 * 3. 自动计算列宽，根据内容长度动态调整
 */
export const convertToHTML = (data: string[][]): string => {
  if (!data || data.length === 0) return '';

  const escapeHtml = (text: string): string => {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  };

  // 计算每列的最小宽度（基于内容长度）
  const calculateColumnWidths = (): number[] => {
    if (data.length === 0) return [];
    
    const columnCount = data[0].length;
    const widths: number[] = new Array(columnCount).fill(100); // 默认最小宽度 100px
    
    // 遍历所有行，计算每列的最大内容长度
    data.forEach(row => {
      row.forEach((cell, colIndex) => {
        // 根据字符数估算宽度：中文字符约 14px，英文字符约 8px
        const chineseChars = (cell.match(/[\u4e00-\u9fa5]/g) || []).length;
        const otherChars = cell.length - chineseChars;
        const estimatedWidth = chineseChars * 14 + otherChars * 8 + 20; // 加上 padding
        
        widths[colIndex] = Math.max(widths[colIndex], Math.min(estimatedWidth, 300)); // 最大 300px
      });
    });
    
    return widths;
  };

  const columnWidths = calculateColumnWidths();

  let html = '<table border="1" cellpadding="5" cellspacing="0" style="border-collapse: collapse; border: 1px solid #ddd; table-layout: auto;">';

  // 添加表头（第一行）
  if (data.length > 0) {
    html += '<thead><tr>';
    data[0].forEach((cell, index) => {
      const width = columnWidths[index] || 120;
      html += `<th style="background-color: #f5f5f5; border: 1px solid #ddd; padding: 10px 12px; text-align: left; font-weight: bold; min-width: ${width}px; white-space: nowrap;">${escapeHtml(cell)}</th>`;
    });
    html += '</tr></thead>';
  }

  // 添加表体（剩余行）
  if (data.length > 1) {
    html += '<tbody>';
    const bodyRows = data.slice(1);
    for (const row of bodyRows) {
      html += '<tr>';
      // eslint-disable-next-line no-loop-func
      row.forEach((cell, index) => {
        const width = columnWidths[index] || 120;
        html += `<td style="border: 1px solid #ddd; padding: 10px 12px; min-width: ${width}px;">${escapeHtml(cell)}</td>`;
      });
      html += '</tr>';
    }
    html += '</tbody>';
  }

  html += '</table>';
  return html;
};

/**
 * 查找表格前的标题和 tid 标签（仅查找当前表格）
 * @description 
 * - 只查找当前表格最近的一个标题元素
 * - 如果该标题内有多个紧挨着的 tid，返回所有这些 tid（用于多 sheet 下载）
 * - 如果只有单个 tid 或无 tid，返回对应的信息
 * 
 * @param tableId - 表格唯一标识符
 * @returns 统一的 tid 查找结果
 * 
 * @example
 * // 单个 tid 场景
 * const result = findPreviousElementTitle('table-1');
 * // { hasTid: true, tids: [{ tableId: 'table-1', tidContent: 'xxx', title: '表格1' }], isMultiple: false }
 * 
 * // 多个 tid 场景（同一标题内紧挨着排列）
 * const result = findPreviousElementTitle('table-1');
 * // { hasTid: true, tids: [tid1, tid2, tid3], isMultiple: true }
 */
export const findPreviousElementTitle = (
  tableId: string
): IFindTidsResult => {
  const defaultResult: IFindTidsResult = { 
    hasTid: false, 
    tids: [], 
    isMultiple: false 
  };
  
  try {
    // 1. 使用 tableId 精确定位当前表格元素
    const currentTable = document.querySelector(`table[data-table-id="${tableId}"]`);
    
    if (!currentTable) {
      console.warn(`未找到 tableId 为 ${tableId} 的表格元素`);
      return defaultResult;
    }
    
    // 2. 获取表格前一个兄弟元素（最近的标题）
    const previousElement = currentTable.parentElement?.previousElementSibling;
    
    if (!previousElement) {
      console.warn(`未找到 tableId 为 ${tableId} 的前置标题元素`);
      return defaultResult;
    }
    
    // 3. 查找该标题内的所有 tid 标签（兼容旧的 <tid> 和新的 <span data-tag-type="tid">）
    // ⚠️ 使用 querySelectorAll 获取所有 tid，因为多个 tid 可能紧挨着排列
    const tidElements = previousElement.querySelectorAll('tid, [data-tag-type="tid"]');
    
    if (tidElements.length === 0) {
      // 没有找到 tid，返回空结果
      return defaultResult;
    }
    
    // 4. 获取标题文本（如果是 h1-h6 标签）
    const tagName = previousElement.tagName?.toLowerCase();
    let baseTitle = '';
    
    if (tagName && /^h[1-6]$/.test(tagName)) {
      // 从标题标签中提取文本，移除所有 tid 标签部分
      const clonedElement = previousElement.cloneNode(true) as HTMLElement;
      const clonedTids = clonedElement.querySelectorAll('tid, [data-tag-type="tid"]');
      clonedTids.forEach(tid => tid.remove());
      baseTitle = clonedElement.textContent?.trim() || '';
    }
    
    // 5. 为每个 tid 创建一个条目
    const tidsInfo: ITidInfo[] = [];
    tidElements.forEach((tidElement, index) => {
      const tidContent = tidElement.textContent?.trim() || '';
      if (!tidContent) return;
      
      // 生成标题：如果有多个 tid，添加序号
      let title = baseTitle || 'file';
      if (tidElements.length > 1) {
        title = `${title} (${index + 1})`;
      }
      
      tidsInfo.push({
        tableId,
        tidContent,
        title: cleanSheetName(title, index),
      });
    });
    
    // 6. 判断返回结果
    if (tidsInfo.length === 0) {
      // 没有有效的 tid，返回空结果
      return defaultResult;
    }
    
    if (tidsInfo.length === 1) {
      // 只有一个 tid，单 tid 模式
      return {
        hasTid: true,
        tids: tidsInfo,
        isMultiple: false,
      };
    }
    
    // 多个 tid，多 tid 模式（同一标题内紧挨着排列）
    return {
      hasTid: true,
      tids: tidsInfo,
      isMultiple: true,
    };
    
  } catch (error) {
    console.warn('[findPreviousElementTitle] 查找失败:', error);
    return defaultResult;
  }
};

/**
 * 清理 sheet 名称
 * @description 
 * - 移除 Excel 不支持的字符：\ / ? * [ ]
 * - 限制长度为 31 字符（Excel 限制）
 * - 处理空名称
 * 
 * @param name - 原始名称
 * @param index - 索引（用于生成默认名称）
 * @returns 清理后的名称
 */
const cleanSheetName = (name: string, index: number): string => {
  // 移除非法字符：\ / ? * [ ] :
  let cleaned = name.replace(/[\\/?*[\]:]/g, '_');
  
  // 移除首尾空格
  cleaned = cleaned.trim();
  
  // 限制长度（Excel 限制 31 字符）
  if (cleaned.length > 31) {
    cleaned = `${cleaned.slice(0, 28)}...`;
  }
  
  // 如果为空，使用默认名称
  if (!cleaned) {
    cleaned = `表格 ${index + 1}`;
  }
  
  return cleaned;
};

/**
 * 生成默认文件名
 * 当没有找到标题时，根据表格在答案中的顺序生成文件名
 * 规则：
 * 1. 查找最近的 dba-answer 容器
 * 2. 在该容器内查找所有没有标题的表格
 * 3. 根据当前表格的位置生成序号
 * 4. 第一个表格：table.xlsx，第二个：table-2.xlsx，以此类推
 */
export const generateDefaultFileName = (tableId: string, answerContainer?: string): string => {
  try {
    // 1. 使用 tableId 精确定位当前表格元素
    const currentTable = document.querySelector(`table[data-table-id="${tableId}"]`);
    if (!currentTable) {
      return 'table.xlsx';
    }

    // 2. 查找最近的 dba-answer 容器
    const currentContainer = currentTable.closest(answerContainer as string);
    if (!answerContainer) {
      return 'table.xlsx';
    }

    // 3. 在该容器内查找所有表格（按DOM顺序）
    const allTables = currentContainer?.querySelectorAll('table[data-table-id]');
    
    // 4. 找出所有没有标题的表格，并记录当前表格的位置
    let tableIndex = 0;
    let currentTableIndex = -1;
    
    allTables?.forEach((table) => {
      // 检查该表格前面是否有 h1-h6 标题
      const previousElement = table.parentElement?.previousElementSibling;
      const tagName = previousElement?.tagName?.toLowerCase();
      const hasTitle = tagName && /^h[1-6]$/.test(tagName);
      
      // 只统计没有标题的表格
      if (!hasTitle) {
        tableIndex += 1;
        
        // 判断是否是当前表格
        if (table.getAttribute('data-table-id') === tableId) {
          currentTableIndex = tableIndex;
        }
      }
    });

    // 5. 生成文件名
    if (currentTableIndex === 1) {
      return 'table.xlsx';
    } if (currentTableIndex > 1) {
      return `table-${currentTableIndex}.xlsx`;
    }
    
    // 兜底：如果没找到，使用默认名称
    return 'table.xlsx';
  } catch (error) {
    console.warn('生成默认文件名失败:', error);
    return 'table.xlsx';
  }
};

/**
 * 复制表格到剪贴板（支持 Excel 和 Word 文档）
 * 复制成功后显示成功提示，2秒后自动消失
 * 
 * 优化点：
 * 1. 同时复制 HTML 和纯文本格式，兼容 Excel 和 Word
 * 2. HTML 格式在 Word 中保持表格样式和格式
 * 3. TSV 格式（Tab分隔）在 Excel 中自动识别为表格
 * 4. 纯文本格式在其他编辑器中可用
 * 5. 完善的兼容性检查，避免 ClipboardItem 未定义错误
 * 6. 多层降级方案，确保在各种浏览器环境中都能正常工作
 */
export const copyTableToClipboard = async (tableData: string[][]): Promise<boolean> => {
  try {
    if (!tableData || tableData.length === 0) {
      console.warn('表格数据为空');
      return false;
    }

    // 生成HTML和TSV格式
    const htmlData = convertToHTML(tableData);
    const tsvData = convertToTSV(tableData);

    // 检查是否支持 ClipboardItem（避免 ReferenceError）
    const supportsClipboardItem = typeof ClipboardItem !== 'undefined';

    // 方案1: 使用 Clipboard API 同时复制多种格式（最佳方案）
    // 支持粘贴到 Word（保持样式）和 Excel（自动识别表格）
    if (navigator.clipboard && supportsClipboardItem) {
      try {
        // 创建包含多种格式的 ClipboardItem
        const htmlBlob = new Blob([htmlData], { type: 'text/html' });
        const textBlob = new Blob([tsvData], { type: 'text/plain' });
        
        const clipboardItem = new ClipboardItem({
          'text/html': htmlBlob,
          'text/plain': textBlob,
        });

        await navigator.clipboard.write([clipboardItem]);
        return true;
      } catch (clipboardError) {
        console.warn('Clipboard API 失败，尝试降级方案:', clipboardError);
      }
    }

    // 方案2: 使用传统的 document.execCommand 方法（降级方案）
    // 支持复制 HTML 格式到 Word
    try {
      const tempDiv = document.createElement('div');
      tempDiv.style.position = 'fixed';
      tempDiv.style.left = '-9999px';
      tempDiv.innerHTML = htmlData;
      document.body.appendChild(tempDiv);

      const range = document.createRange();
      range.selectNodeContents(tempDiv);
      
      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
        selection.addRange(range);
        
        const success = document.execCommand('copy');
        selection.removeAllRanges();
        document.body.removeChild(tempDiv);
        
        if (success) {
          return true;
        }
      }
      
      document.body.removeChild(tempDiv);
    } catch (execCommandError) {
      console.warn('execCommand 方法失败，使用最终降级方案:', execCommandError);
    }

    // 方案3: 最终降级方案 - 只复制纯文本
    return await copyToClipboard(tsvData);
  } catch (error) {
    // 区分错误类型，提供具体的失败原因
    const errorMsg = error instanceof Error ? error.message : String(error);
    console.error('复制表格失败:', errorMsg, error);
    return false;
  }
};

/**
 * 下载表格为Excel文件
 * 
 * 优化点：
 * 1. 使用 tableId 精确定位表格
 * 2. 支持外部传入文件名，避免重复计算
 * 3. 如果找不到标题，根据表格在答案中的顺序生成文件名
 */
export const downloadTableAsExcel = async (
  tableData: string[][],
  tableId: string,
  answerContainer?: string,
  /** 外部传入的文件名（不含扩展名），优先使用 */
  externalTitle?: string
): Promise<void> => {
  try {
    // 动态导入xlsx-js-style库
    const XLSX = await import('xlsx-js-style');

    // 创建工作簿
    const wb = XLSX.utils.book_new();

    // 将数据转换为工作表
    const ws = XLSX.utils.aoa_to_sheet(tableData);

    // 设置列宽（可选）
    const colWidths = tableData[0]?.map(() => ({ wch: 15 })) || [];
    ws['!cols'] = colWidths;

    // 添加工作表到工作簿
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');

    // 生成文件名：优先使用外部传入的标题
    let fileName: string;
    if (externalTitle) {
      // 清理文件名中的非法字符
      const cleanTitle = externalTitle.replace(/[<>:"/\\|?*]/g, '_');
      fileName = `${cleanTitle}.xlsx`;
    } else {
      // 使用智能默认文件名
      fileName = generateDefaultFileName(tableId, answerContainer);
    }

    // 下载文件
    XLSX.writeFile(wb, fileName);
  } catch (error) {
    console.error('下载表格失败:', error);
    throw error;
  }
};
